import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavbarProps {
  cartCount?: number; // kept for backward compat, unused
}

const Navbar = ({ cartCount = 0 }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/products", label: "설치사례" },
    { to: "/categories", label: "제품" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? "bg-white border-b border-stone-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-1.5 cursor-pointer">
          <span className="text-xl font-black tracking-tight text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            도어준
          </span>
          <span className="text-[10px] font-semibold text-stone-400 tracking-widest uppercase mt-1">DOORJUN</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                isActive(link.to)
                  ? "text-stone-900"
                  : scrolled
                  ? "text-stone-500 hover:text-stone-900"
                  : "text-stone-700 hover:text-stone-900"
              }`}
            >
              {link.label}
              {isActive(link.to) && (
                <span className="block h-0.5 bg-stone-900 mt-0.5 rounded-full" />
              )}
            </Link>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-3">
          <a
            href="https://open.kakao.com/o/srLtta1b"
            target="_blank"
            rel="nofollow noreferrer"
            className="hidden md:flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer transition-colors"
            style={{ backgroundColor: "#FEE500", color: "#3A1D1D" }}
          >
            <i className="ri-chat-smile-2-line" />
            무료 상담
          </a>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-9 h-9 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="메뉴 열기"
          >
            <i className={`text-xl text-stone-700 ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium py-2 border-b border-stone-50 cursor-pointer ${
                isActive(link.to) ? "text-stone-900" : "text-stone-500"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};

export default Navbar;
